import { useEffect, useState } from 'react';
import { intakes as intakesApi } from '@/api/endpoints';
import type { Intake } from '@/types';
import { Icon } from './Icon';

interface Props {
  open: boolean;
  intake: Intake;
  onClose: () => void;
  onMerged: () => void;
}

/** „Studnia nr 1” z nazwy „Ujęcie wody Skotniki – studnia nr 1” — podpowiedź oznaczenia studni. */
function guessWell(name: string): string {
  const m = name.match(/(?:[–-]\s*|\b)(studni[ae][^,–]*)$/i) ?? name.match(/^(studnia\s+.+?)(?:\s+[–-]\s+.*)?$/i);
  if (!m) return '';
  const w = m[1].trim();
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/** „Ujęcie wody Skotniki” z „Ujęcie wody Skotniki – studnia nr 1”. */
function guessName(name: string, town: string | null | undefined): string {
  const cut = name.replace(/\s*[–-]\s*studni[ae].*$/i, '').trim();
  if (cut && !/^studnia\b/i.test(cut)) return cut;
  return town ? `Ujęcie wody ${town}` : '';
}

/**
 * Porządkowanie ujęcia założonego jako „studnia”: nazwa całego ujęcia, ujęcia-studnie stają się
 * studniami tego ujęcia, a dołączone ujęcia tego samego klienta trafiają do archiwum
 * (oferty, opracowania i pliki przechodzą na to ujęcie).
 */
export function IntakeMergeModal({ open, intake, onClose, onMerged }: Props) {
  const [candidates, setCandidates] = useState<Intake[]>([]);
  const [name, setName] = useState('');
  const [sourceIds, setSourceIds] = useState<number[]>([]);
  const [wells, setWells] = useState<Record<number, { designation: string; replacement: boolean }>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null); setBusy(false); setSourceIds([]);
    setName(guessName(intake.name, intake.town));
    setWells({ [intake.id]: { designation: guessWell(intake.name), replacement: /zast[eę]pcz|awaryjn/i.test(intake.name) } });
    intakesApi.list().then(list => setCandidates(list.filter(u =>
      u.id !== intake.id && u.client?.id === intake.client.id && u.status !== 'ARCHIVED'))).catch(() => setCandidates([]));
  }, [open, intake]);

  if (!open) return null;

  const toggle = (u: Intake) => {
    setSourceIds(prev => prev.includes(u.id) ? prev.filter(x => x !== u.id) : [...prev, u.id]);
    setWells(prev => prev[u.id] ? prev : { ...prev, [u.id]: { designation: guessWell(u.name), replacement: /zast[eę]pcz|awaryjn/i.test(u.name) } });
  };
  const involved = [intake, ...candidates.filter(u => sourceIds.includes(u.id))];
  // Ujęcie, które ma już otwory, wnosi je bez zmian; ujęcie bez otworów samo staje się studnią.
  const needsWell = involved.filter(u => (u.boreholes?.length ?? 0) === 0);

  const submit = async () => {
    setErr(null);
    if (!name.trim()) { setErr('Podaj nazwę całego ujęcia.'); return; }
    const missing = needsWell.find(u => !wells[u.id]?.designation.trim());
    if (missing) { setErr(`Podaj oznaczenie studni dla ${missing.number}.`); return; }
    setBusy(true);
    try {
      await intakesApi.merge(intake.id, {
        name: name.trim(),
        sourceIds,
        wells: needsWell.map(u => ({
          intakeId: u.id,
          designation: wells[u.id].designation.trim(),
          function: wells[u.id].replacement ? 'REPLACEMENT' : 'PRODUCTION',
        })),
      });
      onMerged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="card w-full max-w-[640px] max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="card-head flex items-center">
          <div className="card-title">Uporządkuj ujęcie</div>
          <button onClick={onClose} className="ml-auto btn btn-ghost btn-icon" title="Zamknij"><Icon name="x"/></button>
        </div>
        <div className="card-body flex flex-col gap-3">
          <div className="text-[12px] text-ink-500">
            Ujęcie to całe ujęcie wody, a studnie są jego częścią. Nadaj nazwę ujęcia bez studni i dołącz
            ujęcia, które w rzeczywistości są studniami tego samego ujęcia — ich oferty, opracowania i pliki
            przejdą tutaj, a one same trafią do archiwum.
          </div>
          <label className="field">
            <span className="field-label">Nazwa ujęcia *</span>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="np. Ujęcie wody Skotniki"/>
          </label>

          <div className="field">
            <span className="field-label">Dołącz ujęcia-studnie klienta {intake.client.name}</span>
            {candidates.length === 0 ? (
              <div className="text-[12px] text-ink-400">Klient nie ma innych ujęć.</div>
            ) : (
              <div className="flex flex-col gap-1">
                {candidates.map(u => (
                  <label key={u.id} className="flex items-center gap-2 text-[13px] cursor-pointer">
                    <input type="checkbox" checked={sourceIds.includes(u.id)} onChange={() => toggle(u)}/>
                    <span className="font-mono text-[12px] text-ink-500">{u.number}</span>
                    <span>{u.name}</span>
                    {(u.boreholes?.length ?? 0) > 0 && <span className="text-[11px] text-ink-400">({u.boreholes.length} otw.)</span>}
                  </label>
                ))}
              </div>
            )}
          </div>

          {needsWell.length > 0 && (
            <div className="field">
              <span className="field-label">Studnie (z ujęć bez otworów — przejmą ich Q i współrzędne)</span>
              <div className="flex flex-col gap-1.5">
                {needsWell.map(u => (
                  <div key={u.id} className="grid grid-cols-[110px_1fr_auto] items-center gap-2">
                    <span className="font-mono text-[12px] text-ink-500" title={u.name}>{u.number}</span>
                    <input className="input" value={wells[u.id]?.designation ?? ''} placeholder="np. Studnia nr 1"
                           onChange={e => setWells(prev => ({ ...prev, [u.id]: { replacement: prev[u.id]?.replacement ?? false, designation: e.target.value } }))}/>
                    <label className="flex items-center gap-1 text-[11px] text-ink-500 whitespace-nowrap">
                      <input type="checkbox" checked={wells[u.id]?.replacement ?? false}
                             onChange={e => setWells(prev => ({ ...prev, [u.id]: { designation: prev[u.id]?.designation ?? '', replacement: e.target.checked } }))}/>
                      zastępcza / awaryjna
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sourceIds.length > 0 && (
            <div className="text-[11px] text-amber-700">
              Po scaleniu wydajność Q ujęcia będzie pusta — uzupełnij ją (Q każdej studni zostaje na jej karcie).
            </div>
          )}
          {err && <div className="text-red-500 text-[12px]">{err}</div>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="btn btn-secondary btn-sm" disabled={busy}>Anuluj</button>
            <button onClick={submit} className="btn btn-primary btn-sm" disabled={busy}>
              {busy ? 'Zapisywanie…' : sourceIds.length ? `Scal ${sourceIds.length + 1} ujęcia` : 'Zapisz'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
